import React, { useCallback, useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { ClipboardCheck, PackageCheck, Truck } from "lucide-react";
import Navbar from "@/components/Navbar";
import { API, useAuth } from "@/context/AuthContext";

const SAMPLE_STATUSES = ["requested", "approved", "dispatched", "received", "revision_requested", "rejected"];
const BUYER_SAMPLE_ACTIONS = {
  requested: ["approved", "rejected", "revision_requested"],
  dispatched: ["received", "revision_requested"],
  received: ["approved", "revision_requested", "rejected"],
};

const ORDER_TRANSITIONS = {
  draft: ["issued", "cancelled"],
  issued: ["confirmed", "cancelled"],
  confirmed: ["in_production", "cancelled"],
  in_production: ["quality_check", "cancelled"],
  quality_check: ["in_production", "ready_to_ship", "cancelled"],
  ready_to_ship: ["shipped", "cancelled"],
  shipped: ["delivered"],
  delivered: ["completed"],
  completed: [],
  cancelled: [],
};

export default function Workflow() {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const [samples, setSamples] = useState([]);
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const [sampleResponse, orderResponse] = await Promise.all([
        axios.get(`${API}/samples`),
        axios.get(`${API}/purchase-orders`),
      ]);
      setSamples(sampleResponse.data || []);
      setOrders(orderResponse.data || []);
    } catch (error) {
      setLoadError(error.response?.data?.detail || "Workflow items could not be loaded.");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const selectedSampleId = searchParams.get("sample_id");
  const selectedSampleStatus = searchParams.get("sample_status");
  const visibleSamples = selectedSampleId
    ? samples.filter((sample) => sample.sample_id === selectedSampleId)
    : selectedSampleStatus && selectedSampleStatus !== "all"
      ? samples.filter((sample) => sample.status === selectedSampleStatus)
      : samples;

  const updateSample = async (sampleId, payload) => {
    try {
      await axios.patch(`${API}/samples/${sampleId}`, payload);
      toast.success("Sample updated");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update sample");
      throw error;
    }
  };

  const updateOrder = async (poId, payload) => {
    try {
      await axios.patch(`${API}/purchase-orders/${poId}`, payload);
      toast.success("Purchase order updated");
      await load();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Unable to update purchase order");
      throw error;
    }
  };

  return (
    <div className="min-h-screen bg-bone">
      <Navbar />
      <main className="max-w-[1400px] mx-auto px-6 lg:px-10 py-12 lg:py-16">
        <Link to={user?.role === "admin" ? "/admin" : "/dashboard"} className="font-mono text-xs underline mb-8 block">
          {user?.role === "admin" ? "← ADMIN" : "← DASHBOARD"}
        </Link>
        <div className="overline mb-3 text-klein">§ OPERATIONS</div>
        <h1 className="font-serif text-5xl font-light leading-none tracking-tight mb-4">Samples &amp; orders.</h1>
        <p className="text-sm text-[--muted-foreground] mb-10">Review sample requests, confirm delivery, and move awarded orders through production and fulfillment.</p>

        {loadError && <div className="mb-6 border border-red-300 bg-red-50 px-4 py-3 text-sm text-red-900">{loadError}</div>}
        {loading && <div className="font-mono text-xs text-[--muted-foreground] mb-6">Loading workflow…</div>}

        <section className="mb-14" aria-labelledby="samples-heading">
          <div className="flex items-center gap-3 border-b border-[--border-soft] pb-3 mb-5">
            <ClipboardCheck className="w-5 h-5 text-klein" />
            <h2 id="samples-heading" className="font-serif text-3xl">Sampling</h2>
            <span className="font-mono text-xs text-[--muted-foreground]">{visibleSamples.length}{visibleSamples.length !== samples.length ? ` / ${samples.length}` : ""}</span>
            {(selectedSampleId || selectedSampleStatus) && <Link to="/workflow" className="ml-auto font-mono text-[10px] underline">All sample requests</Link>}
          </div>
          <div className="space-y-3">
            {visibleSamples.map((sample) => <SampleCard key={sample.sample_id} sample={sample} role={user?.role} onUpdate={updateSample} />)}
            {!loading && visibleSamples.length === 0 && <div className="py-8 text-sm font-mono text-[--muted-foreground]">— No sample requests match this view.</div>}
          </div>
        </section>

        <section aria-labelledby="orders-heading">
          <div className="flex items-center gap-3 border-b border-[--border-soft] pb-3 mb-5">
            <PackageCheck className="w-5 h-5 text-klein" />
            <h2 id="orders-heading" className="font-serif text-3xl">Procurement</h2>
            <span className="font-mono text-xs text-[--muted-foreground]">{orders.length}</span>
          </div>
          <div className="space-y-3">
            {orders.map((order) => <PurchaseOrderCard key={order.po_id} order={order} role={user?.role} onUpdate={updateOrder} />)}
            {!loading && orders.length === 0 && <div className="py-8 text-sm font-mono text-[--muted-foreground]">— No purchase orders yet.</div>}
          </div>
        </section>
      </main>
    </div>
  );
}

function SampleCard({ sample, role, onUpdate }) {
  const [status, setStatus] = useState(sample.status);
  const [busy, setBusy] = useState(false);
  const [dispatch, setDispatch] = useState(sample.dispatch_details || {});
  const [receipt, setReceipt] = useState(sample.receipt_details || {});
  const [revisionNotes, setRevisionNotes] = useState(sample.revision_notes || "");
  const [rejectionReason, setRejectionReason] = useState(sample.rejection_reason || "");
  const isAdmin = role === "admin";
  const choices = isAdmin ? SAMPLE_STATUSES : role === "buyer"
    ? BUYER_SAMPLE_ACTIONS[sample.status] || []
    : ["approved", "revision_requested"].includes(sample.status) ? ["dispatched"] : [];

  useEffect(() => {
    setStatus(sample.status);
    setDispatch(sample.dispatch_details || {});
    setReceipt(sample.receipt_details || {});
  }, [sample]);

  const submit = async (event) => {
    event.preventDefault();
    const payload = { status };
    if (status === "dispatched") {
      if (!dispatch.carrier?.trim() && !dispatch.tracking_number?.trim()) {
        toast.error("Add a carrier or tracking number before dispatching");
        return;
      }
      payload.dispatch_details = { ...dispatch, dispatched_at: new Date().toISOString() };
    }
    if (status === "received") {
      if (!receipt.received_by?.trim() && !receipt.condition?.trim()) {
        toast.error("Add who received the sample or its condition");
        return;
      }
      payload.receipt_details = { ...receipt, received_at: receipt.received_at || new Date().toISOString() };
    }
    if (status === "revision_requested") payload.revision_notes = revisionNotes;
    if (status === "rejected") payload.rejection_reason = rejectionReason;
    if (status === "revision_requested" && !revisionNotes.trim()) {
      toast.error("Describe what needs to change before requesting a revision");
      return;
    }
    if (status === "rejected" && !rejectionReason.trim()) {
      toast.error("Add a reason before rejecting the sample");
      return;
    }
    setBusy(true);
    try { await onUpdate(sample.sample_id, payload); }
    catch { setStatus(sample.status); }
    finally { setBusy(false); }
  };

  return (
    <article id={`sample-${sample.sample_id}`} className="border border-[--border-soft] bg-white/55 p-5 scroll-mt-24" data-testid={`sample-${sample.sample_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-xl">{sample.description || "Production sample"}</div>
          <div className="font-mono text-[10px] text-[--muted-foreground] mt-1">{sample.sample_id} · RFQ {sample.rfq_id} · Quote {sample.quote_id}</div>
        </div>
        <span className={`tag ${sample.status === "received" || sample.status === "approved" ? "verified" : ""}`}>{formatStatus(sample.status)}</span>
      </div>

      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4 text-sm mb-4">
        <WorkflowValue label="Quantity" value={sample.quantity} />
        <WorkflowValue label="Manufacturer" value={sample.agent_id} />
        <WorkflowValue label="Created" value={sample.created_at ? new Date(sample.created_at).toLocaleDateString() : "—"} />
        <WorkflowValue label="Specifications" value={Object.entries(sample.specifications || {}).map(([key, value]) => `${key}: ${value}`).join(" · ") || "—"} />
      </div>

      {sample.dispatch_details && Object.keys(sample.dispatch_details).length > 0 && <DetailBlock title="DISPATCH" details={sample.dispatch_details} />}
      {sample.receipt_details && Object.keys(sample.receipt_details).length > 0 && <DetailBlock title="RECEIPT" details={sample.receipt_details} />}
      {sample.revision_notes && <p className="text-sm mb-3"><span className="overline text-[10px] mr-2">REVISION</span>{sample.revision_notes}</p>}
      {sample.rejection_reason && <p className="text-sm mb-3"><span className="overline text-[10px] mr-2">REASON</span>{sample.rejection_reason}</p>}
      {(sample.status_history || []).length > 0 && <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] text-[--muted-foreground] mt-3">
        {sample.status_history.map((event, index) => <span key={`${event.status}-${index}`}>{formatStatus(event.status)} · {event.changed_at ? new Date(event.changed_at).toLocaleDateString() : "—"}</span>)}
      </div>}

      {choices.length > 0 ? (
        <form onSubmit={submit} className="border-t border-[--border-soft] pt-4 mt-4 space-y-3">
          <div className="flex flex-wrap items-end gap-3">
            <label className="block w-full sm:w-auto">
              <span className="overline text-[10px] block mb-1">NEXT STATUS</span>
              <select className="input-underline w-full sm:min-w-[190px] text-sm" value={status} onChange={(event) => setStatus(event.target.value)}>
                {choices.map((choice) => <option key={choice} value={choice}>{formatStatus(choice)}</option>)}
              </select>
            </label>
            {status === "dispatched" && <>
              <input className="input-underline w-full sm:w-40 text-sm" value={dispatch.carrier || ""} onChange={(event) => setDispatch({ ...dispatch, carrier: event.target.value })} placeholder="Carrier" aria-label="Sample carrier" />
              <input className="input-underline w-full sm:w-48 text-sm" value={dispatch.tracking_number || ""} onChange={(event) => setDispatch({ ...dispatch, tracking_number: event.target.value })} placeholder="Tracking number" aria-label="Sample tracking number" />
            </>}
            {status === "received" && <>
              <input className="input-underline w-full sm:w-40 text-sm" value={receipt.received_by || ""} onChange={(event) => setReceipt({ ...receipt, received_by: event.target.value })} placeholder="Received by" aria-label="Sample received by" />
              <input className="input-underline w-full sm:w-48 text-sm" value={receipt.condition || ""} onChange={(event) => setReceipt({ ...receipt, condition: event.target.value })} placeholder="Condition / notes" aria-label="Sample condition" />
            </>}
            {status === "revision_requested" && <input className="input-underline w-full sm:flex-1 text-sm" value={revisionNotes} onChange={(event) => setRevisionNotes(event.target.value)} placeholder="Describe the requested revision" aria-label="Sample revision notes" />}
            {status === "rejected" && <input className="input-underline w-full sm:flex-1 text-sm" value={rejectionReason} onChange={(event) => setRejectionReason(event.target.value)} placeholder="Reason for rejection" aria-label="Sample rejection reason" />}
            <button disabled={busy || status === sample.status} className="btn-primary text-xs w-full sm:w-auto">{busy ? "Saving…" : "Update sample"}</button>
          </div>
        </form>
      ) : (
        <div className="border-t border-[--border-soft] pt-3 mt-4 font-mono text-[10px] text-[--muted-foreground]">
            {role === "agent" && sample.status === "requested" ? "Awaiting buyer approval before dispatch." : "No action is available for your role at this stage."}
        </div>
      )}
    </article>
  );
}

function PurchaseOrderCard({ order, role, onUpdate }) {
  const [status, setStatus] = useState("");
  const [busy, setBusy] = useState(false);
  const [shipping, setShipping] = useState(order.shipping_details || {});
  const [delivery, setDelivery] = useState(order.delivery_details || {});
  const [notes, setNotes] = useState(order.notes || "");
  const isAdmin = role === "admin";
  const allTransitions = ORDER_TRANSITIONS[order.status] || [];
  const choices = isAdmin ? allTransitions : role === "buyer"
    ? allTransitions.filter((next) => next === "delivered")
    : allTransitions.filter((next) => ["confirmed", "in_production", "quality_check", "ready_to_ship", "shipped"].includes(next));
  const selectedStatus = status || choices[0] || order.status;

  useEffect(() => {
    setStatus("");
    setShipping(order.shipping_details || {});
    setDelivery(order.delivery_details || {});
    setNotes(order.notes || "");
  }, [order]);

  const submit = async (event) => {
    event.preventDefault();
    const payload = { status: selectedStatus, notes };
    if (["ready_to_ship", "shipped"].includes(selectedStatus)) {
      if (!shipping.carrier?.trim() && !shipping.tracking_number?.trim()) {
        toast.error("Add a carrier or tracking number before shipping");
        return;
      }
      payload.shipping_details = {
        ...shipping,
        ...(selectedStatus === "shipped"
          ? { shipped_at: new Date().toISOString() }
          : { ready_to_ship_at: new Date().toISOString() }),
      };
    }
    if (["delivered", "completed"].includes(selectedStatus)) {
      if (!delivery.received_by?.trim() && !delivery.condition?.trim()) {
        toast.error("Add who received the order or its delivery condition");
        return;
      }
      payload.delivery_details = { ...delivery, received_at: delivery.received_at || new Date().toISOString() };
    }
    setBusy(true);
    try { await onUpdate(order.po_id, payload); }
    catch { setStatus(""); }
    finally { setBusy(false); }
  };

  return (
    <article className="border border-[--border-soft] bg-white/55 p-5" data-testid={`purchase-order-${order.po_id}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 mb-4">
        <div>
          <div className="font-serif text-xl">Purchase order {order.po_id}</div>
          <div className="font-mono text-[10px] text-[--muted-foreground] mt-1">RFQ {order.rfq_id} · Quote {order.quote_id}</div>
        </div>
        <span className={`tag ${["delivered", "completed"].includes(order.status) ? "verified" : ""}`}>{formatStatus(order.status)}</span>
      </div>

      <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-4">
        <WorkflowValue label="Quantity" value={order.quantity} />
        <WorkflowValue label="Unit price" value={`₹${Number(order.unit_price_usd || 0).toLocaleString()}`} />
        <WorkflowValue label="Order total" value={`₹${Number(order.total_price_usd || 0).toLocaleString()}`} />
        <WorkflowValue label="Manufacturer" value={order.agent_id} />
      </div>

      {(order.sku_quotes || []).length > 0 && <div className="overflow-x-auto mb-4">
        <table className="w-full min-w-[520px] text-xs">
          <thead><tr className="border-b border-[--border-soft] text-left font-mono uppercase text-[--muted-foreground]">
            <th className="p-2">SKU</th><th className="p-2">Product</th><th className="p-2">Qty</th><th className="p-2">Price / unit</th><th className="p-2">Line total</th><th className="p-2">Lead time</th>
          </tr></thead>
          <tbody>{order.sku_quotes.map((line, index) => <tr key={line.sku || index} className="border-b border-[--border-soft] last:border-0">
            <td className="p-2 font-mono">{line.sku || "—"}</td><td className="p-2">{line.product_name || "—"}</td><td className="p-2">{line.quantity}</td>
            <td className="p-2">₹{line.unit_price_usd}</td><td className="p-2">₹{Number(line.total_price_usd || 0).toLocaleString()}</td><td className="p-2">{line.lead_time_days ?? "—"} days</td>
          </tr>)}</tbody>
        </table>
      </div>}

      {order.notes && <p className="text-sm text-[--muted-foreground] mb-3">{order.notes}</p>}
      {order.shipping_details && Object.keys(order.shipping_details).length > 0 && <DetailBlock title="SHIPPING" details={order.shipping_details} />}
      {order.delivery_details && Object.keys(order.delivery_details).length > 0 && <DetailBlock title="DELIVERY" details={order.delivery_details} />}
      {(order.status_history || []).length > 0 && <div className="flex flex-wrap gap-x-4 gap-y-1 font-mono text-[10px] text-[--muted-foreground] mt-3">
        {order.status_history.map((event, index) => <span key={`${event.status}-${index}`}>{formatStatus(event.status)} · {event.changed_at ? new Date(event.changed_at).toLocaleDateString() : "—"}</span>)}
      </div>}

      {choices.length > 0 && <form onSubmit={submit} className="border-t border-[--border-soft] pt-4 mt-4 space-y-3">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block w-full sm:w-auto">
            <span className="overline text-[10px] block mb-1">NEXT STATUS</span>
            <select className="input-underline w-full sm:min-w-[190px] text-sm" value={selectedStatus} onChange={(event) => setStatus(event.target.value)}>
              {choices.map((choice) => <option key={choice} value={choice}>{formatStatus(choice)}</option>)}
            </select>
          </label>
          {["ready_to_ship", "shipped"].includes(selectedStatus) && <>
            <input className="input-underline w-full sm:w-40 text-sm" value={shipping.carrier || ""} onChange={(event) => setShipping({ ...shipping, carrier: event.target.value })} placeholder="Carrier" aria-label="Order carrier" />
            <input className="input-underline w-full sm:w-48 text-sm" value={shipping.tracking_number || ""} onChange={(event) => setShipping({ ...shipping, tracking_number: event.target.value })} placeholder="Tracking number" aria-label="Order tracking number" />
          </>}
          {["delivered", "completed"].includes(selectedStatus) && <>
            <input className="input-underline w-full sm:w-40 text-sm" value={delivery.received_by || ""} onChange={(event) => setDelivery({ ...delivery, received_by: event.target.value })} placeholder="Received by" aria-label="Order received by" />
            <input className="input-underline w-full sm:w-48 text-sm" value={delivery.condition || ""} onChange={(event) => setDelivery({ ...delivery, condition: event.target.value })} placeholder="Delivery condition" aria-label="Order delivery condition" />
          </>}
          <input className="input-underline w-full sm:flex-1 text-sm" value={notes} onChange={(event) => setNotes(event.target.value)} placeholder="Order notes" aria-label="Purchase order notes" />
          <button disabled={busy} className="btn-primary text-xs w-full sm:w-auto">{busy ? "Saving…" : role === "buyer" && selectedStatus === "delivered" ? "Confirm delivery" : "Update order"}</button>
        </div>
      </form>}
    </article>
  );
}

function WorkflowValue({ label, value }) {
  return <div className="min-w-0"><div className="overline text-[10px] mb-1">{label}</div><div className="text-sm break-words">{value || "—"}</div></div>;
}

function DetailBlock({ title, details }) {
  const entries = Object.entries(details || {}).filter(([, value]) => value !== "" && value != null);
  if (entries.length === 0) return null;
  return <div className="border-t border-[--border-soft] pt-3 mt-3 mb-3">
    <div className="overline text-[10px] mb-2">{title}</div>
    <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs">{entries.map(([key, value]) => <span key={key}><span className="text-[--muted-foreground]">{formatStatus(key)}:</span> {typeof value === "object" ? JSON.stringify(value) : String(value)}</span>)}</div>
  </div>;
}

function formatStatus(value) {
  return String(value || "").replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}